import * as React from "react";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";

type FilterChipProps = Omit<React.ComponentProps<"button">, "onChange"> & {
  active?: boolean;
  count?: number;
  /** Shows a clear affordance while active. */
  onClear?: () => void;
};

/**
 * A 28px `rounded-full` quick-filter toggle ("Status Ended", "Expiring in
 * 90 Days"). Active chips take `fill-2` and `text-fg`; the accent stays
 * reserved for the one primary action.
 */
function FilterChip({
  active = false,
  count,
  onClear,
  className,
  children,
  ...props
}: FilterChipProps) {
  return (
    <button
      type="button"
      data-slot="filter-chip"
      aria-pressed={active}
      className={cn(
        "inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-sm whitespace-nowrap text-fg-2 inset-ring inset-ring-line-2 transition-colors duration-100 outline-none hover:bg-fill-1 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50 aria-pressed:bg-fill-2 aria-pressed:text-fg aria-pressed:inset-ring-line-3 [&_svg]:size-3.5 [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      {typeof count === "number" ? (
        <span className="text-fg-4 tabular-nums">{count}</span>
      ) : null}
      {active && onClear ? (
        <span
          role="button"
          tabIndex={-1}
          aria-label="Clear Filter"
          className="-mr-1 flex size-4 items-center justify-center rounded-full text-fg-3 hover:bg-fill-2 hover:text-fg"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
        >
          <XIcon className="size-3!" />
        </span>
      ) : null}
    </button>
  );
}

export { FilterChip };
export type { FilterChipProps };
